import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  ToggleButton,
  Typography,
} from '@mui/material';
import { orange } from '@mui/material/colors';

import OtaVsSellCell from './OtaVsSellCell';
import { BASIC_CATEGORY_INDEX, CELL_SX, HEADER_SX, PACKAGE_CATEGORIES } from './packageCategories';
import { ASIA_ONLY_LOS, takenRoomAssetKeys } from './packageOptionsModel';
import { assetKey } from './types';
import type { CategoryRoom, RoomAssetLink, RoomPricing } from './types';

const EXTRA_NIGHT_DIVIDER_SX = { borderLeft: '3px solid', borderLeftColor: orange[700] };

const EDITABLE_OUTLINE_SX = {
  '& .MuiOutlinedInput-root:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': { borderColor: '#000' },
};

const NO_SPINNER_SX = {
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
};

const PRICE_INPUT_SX = { ...EDITABLE_OUTLINE_SX, ...NO_SPINNER_SX };

const COL_WIDTH = 110;
const SINGLE_LOS_COL_WIDTH = 190;
const ROOM_TYPE_LABEL_COL_WIDTH = 60;
const ROOM_TYPE_NAME_COL_WIDTH = 220;

type PricingTableProps = {
  assets: Array<RoomAssetLink>;
  netCurrency: string;
  onDeleteRoomType: (categoryIndex: number, id: number) => void;
  onSelectAsset: (categoryIndex: number, id: number, asset: RoomAssetLink) => void;
  onSelectedLosChange: (nights: number) => void;
  onToggleAsiaOnly: (categoryIndex: number, id: number) => void;
  onUpdateRoomPricing: (id: number, defaultOccupancy: string, changes: Partial<RoomPricing>) => void;
  onSetRoomSellPrice: (id: number, defaultOccupancy: string, nights: number, value: string) => void;
  onSetRoomNetRate: (id: number, defaultOccupancy: string, nights: number, value: string) => void;
  pricingByRoomId: Record<number, RoomPricing>;
  roomsByCategory: Array<Array<CategoryRoom>>;
  selectedLos: number;
  sortedLos: Array<number>;
};

export default function PricingTable({
  assets,
  netCurrency,
  onDeleteRoomType,
  onSelectAsset,
  onSelectedLosChange,
  onToggleAsiaOnly,
  onUpdateRoomPricing,
  onSetRoomSellPrice,
  onSetRoomNetRate,
  pricingByRoomId,
  roomsByCategory,
  selectedLos,
  sortedLos,
}: PricingTableProps) {
  const totalRoomCount = roomsByCategory.reduce((total, rooms) => total + rooms.length, 0);
  const basicRooms = roomsByCategory[BASIC_CATEGORY_INDEX];

  // Non-BASIC rows are read-only and mirror the allocation of the BASIC row with the same asset,
  // since a room type's allocation is set once, on BASIC, and shared across categories.
  function allocationForAsset(assetName: string | undefined) {
    const basicRoom = basicRooms.find((room) => room.assetName === assetName);
    return basicRoom === undefined ? '' : (pricingByRoomId[basicRoom.id]?.allocation ?? '');
  }

  const priceColWidth = sortedLos.length === 1 ? SINGLE_LOS_COL_WIDTH : COL_WIDTH;
  const colWidths = [
    ROOM_TYPE_LABEL_COL_WIDTH,
    ROOM_TYPE_NAME_COL_WIDTH,
    COL_WIDTH,
    COL_WIDTH,
    ...sortedLos.flatMap(() => [priceColWidth, priceColWidth]),
    COL_WIDTH,
    COL_WIDTH,
    COL_WIDTH,
    COL_WIDTH,
  ];

  return (
    <TableContainer sx={{ mt: 3, maxWidth: '100%', overflowX: 'auto', width: 'auto' }}>
      <Table size="small" sx={{ tableLayout: 'fixed', width: 'max-content' }}>
        <colgroup>
          {colWidths.map((width, index) => (
            <col key={index} style={{ width }} />
          ))}
        </colgroup>
        <TableBody>
          <TableRow>
            <TableCell colSpan={2} sx={CELL_SX} />
            <TableCell align="center" colSpan={2} sx={HEADER_SX}>
              PACKAGE
            </TableCell>
            <TableCell align="center" colSpan={sortedLos.length} sx={{ ...HEADER_SX, whiteSpace: 'nowrap' }}>
              SELL PRICE (AUD)
            </TableCell>
            <TableCell align="center" colSpan={sortedLos.length} sx={{ ...HEADER_SX, whiteSpace: 'nowrap' }}>
              NET RATE ({netCurrency})
            </TableCell>
            <TableCell align="center" colSpan={2} sx={{ ...HEADER_SX, ...EXTRA_NIGHT_DIVIDER_SX, color: orange[700] }}>
              EXTRA NIGHT
            </TableCell>
            <TableCell align="center" colSpan={2} sx={HEADER_SX}>
              <Stack alignItems="center" spacing={0.5}>
                <Typography fontWeight={600} variant="caption">
                  OTA COMPARISON
                </Typography>
                <Stack alignItems="center" direction="row" spacing={1}>
                  <Typography color="text.secondary" variant="body2">
                    LOS
                  </Typography>
                  <Select
                    onChange={(e) => onSelectedLosChange(Number(e.target.value))}
                    size="small"
                    value={selectedLos}
                  >
                    {sortedLos.map((nights) => (
                      <MenuItem key={nights} value={nights}>
                        {nights}N
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
              </Stack>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="center" colSpan={2} sx={HEADER_SX}>
              ROOM TYPE
            </TableCell>
            <TableCell align="center" sx={HEADER_SX}>
              ALLOCATION
            </TableCell>
            <TableCell align="center" sx={HEADER_SX}>
              OCCUPANCY
            </TableCell>
            {sortedLos.map((nights) => (
              <TableCell align="center" key={`sell-${nights}`} sx={HEADER_SX}>
                {nights}N
              </TableCell>
            ))}
            {sortedLos.map((nights) => (
              <TableCell align="center" key={`net-${nights}`} sx={HEADER_SX}>
                {nights}N
              </TableCell>
            ))}
            <TableCell align="center" sx={{ ...HEADER_SX, ...EXTRA_NIGHT_DIVIDER_SX }}>
              SELL (AUD)
            </TableCell>
            <TableCell align="center" sx={HEADER_SX}>
              NET ({netCurrency})
            </TableCell>
            <TableCell align="center" sx={HEADER_SX}>
              OTA PRICE
            </TableCell>
            <TableCell align="center" sx={{ ...HEADER_SX, whiteSpace: 'nowrap' }}>
              % VS {selectedLos}N
            </TableCell>
          </TableRow>
          {PACKAGE_CATEGORIES.map((category, categoryIndex) => {
            const rooms = roomsByCategory[categoryIndex];
            if (categoryIndex !== BASIC_CATEGORY_INDEX && rooms.every((room) => !room.assetName)) {
              return null;
            }
            const rowCellSx = { ...CELL_SX, bgcolor: category.bgColor };
            const labelSx = {
              ...rowCellSx,
              color: category.labelColor,
              transform: 'rotate(180deg)',
              writingMode: 'vertical-lr' as const,
            };
            const { defaultOccupancy } = category;

            return rooms.map(({ id, assetName, assetSfId, asiaOnly }, index) => {
              const pricing = pricingByRoomId[id];
              const isBaseRoom = categoryIndex === BASIC_CATEGORY_INDEX;
              const takenAssetKeys = takenRoomAssetKeys(basicRooms, id);

              return (
                <TableRow key={id}>
                  {index === 0 && (
                    <TableCell align="center" rowSpan={rooms.length} sx={labelSx}>
                      {category.label}
                    </TableCell>
                  )}
                  <TableCell sx={rowCellSx}>
                    {isBaseRoom ? (
                      <>
                        <Select
                          displayEmpty
                          error={!assetName}
                          fullWidth
                          onChange={(e) => {
                            const asset = assets.find((candidate) => assetKey(candidate) === e.target.value);
                            if (asset && !takenAssetKeys.has(assetKey(asset))) {
                              onSelectAsset(categoryIndex, id, asset);
                            }
                          }}
                          renderValue={(value) => (value ? assetName : 'Select asset...')}
                          size="small"
                          sx={EDITABLE_OUTLINE_SX}
                          value={assetSfId ?? assetName}
                        >
                          {assets.map((asset) => {
                            const isTaken = takenAssetKeys.has(assetKey(asset));

                            return (
                              <MenuItem disabled={isTaken} key={assetKey(asset)} value={assetKey(asset)}>
                                {isTaken ? `${asset.name} (already used)` : asset.name}
                              </MenuItem>
                            );
                          })}
                        </Select>
                        <ToggleButton
                          color="primary"
                          disabled={!assetName}
                          fullWidth
                          onChange={() => onToggleAsiaOnly(categoryIndex, id)}
                          selected={asiaOnly}
                          size="small"
                          sx={{ borderColor: '#000', mt: 1 }}
                          value="asia-only"
                        >
                          Asia only
                        </ToggleButton>
                      </>
                    ) : (
                      <Typography variant="body2">{assetName || '—'}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={rowCellSx}>
                    {(() => {
                      const allocation = isBaseRoom ? (pricing?.allocation ?? '') : allocationForAsset(assetName);
                      const isNumeric = /^\d*$/.test(allocation);
                      return (
                        <TextField
                          disabled={!isBaseRoom}
                          fullWidth={isNumeric}
                          onChange={(e) => onUpdateRoomPricing(id, defaultOccupancy, { allocation: e.target.value })}
                          placeholder="e.g. 5"
                          size="small"
                          sx={{ ...EDITABLE_OUTLINE_SX, ...(isNumeric ? {} : { width: 250 }) }}
                          value={allocation}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={rowCellSx}>
                    <TextField
                      fullWidth
                      onChange={(e) => onUpdateRoomPricing(id, defaultOccupancy, { occupancy: e.target.value })}
                      placeholder="e.g. 2A"
                      size="small"
                      sx={EDITABLE_OUTLINE_SX}
                      value={pricing?.occupancy ?? defaultOccupancy}
                    />
                  </TableCell>
                  {sortedLos.map((nights) => (
                    <TableCell key={`sell-input-${nights}`} sx={rowCellSx}>
                      {asiaOnly && nights !== ASIA_ONLY_LOS ? (
                        <TextField disabled fullWidth size="small" value="-" />
                      ) : (
                        <TextField
                          fullWidth
                          inputProps={{ min: 0 }}
                          onChange={(e) => onSetRoomSellPrice(id, defaultOccupancy, nights, e.target.value)}
                          size="small"
                          sx={PRICE_INPUT_SX}
                          type="number"
                          value={pricing?.sellByLos[nights] ?? ''}
                        />
                      )}
                    </TableCell>
                  ))}
                  {sortedLos.map((nights) => (
                    <TableCell key={`net-input-${nights}`} sx={rowCellSx}>
                      {asiaOnly && nights !== ASIA_ONLY_LOS ? (
                        <TextField disabled fullWidth size="small" value="-" />
                      ) : (
                        <TextField
                          fullWidth
                          inputProps={{ min: 0 }}
                          onChange={(e) => onSetRoomNetRate(id, defaultOccupancy, nights, e.target.value)}
                          size="small"
                          sx={PRICE_INPUT_SX}
                          type="number"
                          value={pricing?.netByLos[nights] ?? ''}
                        />
                      )}
                    </TableCell>
                  ))}
                  <TableCell sx={{ ...rowCellSx, ...EXTRA_NIGHT_DIVIDER_SX }}>
                    <TextField
                      fullWidth
                      inputProps={{ min: 0 }}
                      onChange={(e) =>
                        onUpdateRoomPricing(id, defaultOccupancy, {
                          extraNightSell: e.target.value,
                          extraNightSellEdited: e.target.value.trim() !== '',
                        })
                      }
                      size="small"
                      sx={PRICE_INPUT_SX}
                      type="number"
                      value={pricing?.extraNightSell ?? ''}
                    />
                  </TableCell>
                  <TableCell sx={rowCellSx}>
                    <TextField
                      fullWidth
                      inputProps={{ min: 0 }}
                      onChange={(e) => onUpdateRoomPricing(id, defaultOccupancy, { extraNightNet: e.target.value })}
                      size="small"
                      sx={PRICE_INPUT_SX}
                      type="number"
                      value={pricing?.extraNightNet ?? ''}
                    />
                  </TableCell>
                  <TableCell align="center" sx={rowCellSx}>
                    {isBaseRoom && (
                      <TextField
                        fullWidth
                        inputProps={{ min: 0 }}
                        onChange={(e) => onUpdateRoomPricing(id, defaultOccupancy, { otaPrice: e.target.value })}
                        size="small"
                        sx={PRICE_INPUT_SX}
                        type="number"
                        value={pricing?.otaPrice ?? ''}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ ...rowCellSx, position: 'relative' }}>
                    {isBaseRoom && <OtaVsSellCell pricing={pricing} selectedLos={selectedLos} />}
                    {totalRoomCount > 1 && (
                      <IconButton
                        onClick={() => onDeleteRoomType(categoryIndex, id)}
                        size="small"
                        sx={{ position: 'absolute', right: -40, top: '50%', transform: 'translateY(-50%)' }}
                      >
                        <CloseIcon color="error" fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            });
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
